import styled from '@emotion/styled';
import { Button, Tabs } from 'antd';
import { sumBy } from 'lodash';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { FaArrowLeft, FaBalanceScale, FaCheckCircle, FaDownload, FaExclamationTriangle } from 'react-icons/fa';
import { FiRefreshCcw } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ConnectedUserEntity } from '../../../../entities/ConnectedUserEntity';
import { PRIMARY, YELLOW } from '../../../../shared/colors';
import { formatNum } from '../../../../shared/constants';
import { ExportFormatButton } from '../../../../shared/ExportFormatButton';
import { getExportMetadata } from '../../../../shared/exportFiles';
import { Layout } from '../../../shared/Layout';
import { BGGlobale } from '../components/Balances/globale';
import { BGTitre } from '../components/Balances/titre';
import { BGTitulaire } from '../components/Balances/titulaire';
import { BGTresor } from '../components/Balances/tresor';
import { exportBGData } from '../components/exportDataExcel';
import { getBalance } from '../network';

const Container = styled.div`
  min-height: calc(100vh - 76px);
  background:
    radial-gradient(circle at 8% 0%, rgba(255, 79, 31, 0.08), transparent 24%),
    radial-gradient(circle at 90% 8%, rgba(255, 176, 0, 0.08), transparent 20%),
    var(--mt-bg);
  color: var(--mt-text);
  font-family: Poppins-R;

  .bread-crum span {
    color: var(--mt-text);
  }

  td.ant-table-column-sort {
    background-color: transparent !important;
  }

  th {
    background-color: ${PRIMARY} !important;
    color: #fff7e8 !important;
    font-weight: bold;
  }

  .odd-row {
    background-color: var(--mt-row-odd);
    color: var(--mt-text);
  }

  .even-row {
    background-color: var(--mt-row-even);
    color: var(--mt-text);
  }

  .odd-row,
  .even-row {
    &:hover > td {
      background-color: var(--mt-row-hover) !important;
      cursor: pointer;
      color: var(--mt-text);
    }
  }

  .content {
    padding: 12px;
    width: 100%;
    color: var(--mt-text);
  }

  .balance-hero {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 18px;
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.13), transparent 38%),
      ${PRIMARY};
    box-shadow: var(--mt-card-shadow);
  }

  .hero-copy {
    display: flex;
    align-items: flex-start;
    gap: 14px;
  }

  .back-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border: 1px solid rgba(255, 176, 0, 0.24);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.06);
    color: ${YELLOW};
    cursor: pointer;
  }

  .eyebrow {
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .balance-hero h1 {
    margin: 5px 0 0;
    color: #fff7e8;
    font-family: Poppins-B;
    font-size: 30px;
    line-height: 1.1;
  }

  .balance-hero p {
    max-width: 760px;
    margin: 8px 0 0;
    color: rgba(255, 247, 232, 0.72);
    line-height: 1.5;
  }

  .page-toolbar,
  .hero-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .ghost-action,
  .primary-action {
    border-radius: 999px;
    font-family: Poppins-B;
  }

  .ghost-action {
    border-color: rgba(255, 176, 0, 0.28);
    background: rgba(255, 255, 255, 0.06);
    color: #fff7e8;
  }

  .primary-action {
    border: 0;
    background: linear-gradient(135deg, #ff4f1f, ${YELLOW});
    color: #07141c;
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    margin-top: 12px;
  }

  .kpi-card,
  .control-panel,
  .balance-shell {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    background: var(--mt-card);
    box-shadow: var(--mt-card-shadow);
  }

  .control-panel {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-top: 12px;
    padding: 14px;
  }

  .control-copy {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .control-icon {
    display: grid;
    place-items: center;
    width: 42px;
    height: 42px;
    border-radius: 8px;
    color: ${YELLOW};
    background: rgba(255, 176, 0, 0.12);
  }

  .control-copy strong {
    display: block;
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .control-copy span {
    color: var(--mt-muted);
    font-size: 12px;
  }

  .control-status {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border-radius: 999px;
    padding: 8px 12px;
    font-family: Poppins-B;
  }

  .control-status.ok {
    color: #00965d;
    background: rgba(0, 150, 93, 0.1);
  }

  .control-status.warning {
    color: #dc2626;
    background: rgba(220, 38, 38, 0.1);
  }

  .kpi-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 78px;
    padding: 13px;
  }

  .kpi-card span {
    color: var(--mt-muted);
    font-size: 11px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .kpi-card strong {
    display: block;
    margin-top: 6px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 21px;
  }

  .kpi-card .icon {
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border: 1px solid rgba(255, 176, 0, 0.24);
    border-radius: 8px;
    color: var(--mt-warning);
    background: var(--mt-surface);
  }

  .balance-shell {
    margin-top: 12px;
    overflow: hidden;
  }

  .balance-shell .ant-tabs-nav {
    margin: 0;
    padding: 0 14px;
    background: ${PRIMARY};
  }

  .balance-shell .ant-tabs-tab {
    color: rgba(255, 247, 232, 0.74);
    font-family: Poppins-B;
  }

  .balance-shell .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${YELLOW} !important;
  }

  .balance-shell .ant-tabs-ink-bar {
    background: ${YELLOW};
  }

  .balance-shell .ant-tabs-content-holder {
    padding: 14px;
  }

  .balance-table .ant-table {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    overflow: hidden;
    background: var(--mt-card);
    color: var(--mt-text);
  }

  .balance-table .ant-table-thead > tr > th {
    border-bottom: 1px solid var(--mt-border);
    font-size: 11px;
    text-transform: uppercase;
  }

  .balance-table .ant-table-tbody > tr > td {
    border-bottom: 1px solid var(--mt-border);
    color: var(--mt-text);
  }

  .balance-table .total-row > td {
    background: rgba(255, 176, 0, 0.09) !important;
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .amount-cell {
    display: block;
    text-align: right;
    font-family: Poppins-B;
    white-space: nowrap;
  }

  .ant-tabs-nav-list {
    min-width: max-content;
  }

  @media (max-width: 760px) {
    .content {
      padding: 18px 12px;
    }

    .balance-hero {
      align-items: stretch;
      flex-direction: column;
    }

    .kpi-grid {
      grid-template-columns: 1fr;
    }

    .control-panel {
      align-items: stretch;
      flex-direction: column;
    }
  }
`;

const KpiCard = ({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string | number;
}) => (
  <div className='kpi-card'>
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
    <div className='icon'>{icon}</div>
  </div>
);

export const BalanceGeneralPage = () => {
  const [tabIndex, setTabIndex] = useState('1');
  const [balances, setBalances] = useState<any>();
  const history = useHistory();
  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;

  const fetchData = () => {
    getBalance(connectedUser.id).then(({ data }) => {
      setBalances(data);
    });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const balanceStats = useMemo(() => {
    const globale = balances?.globale || [];
    const titre = balances?.titre || [];

    return {
      credit: sumBy(globale, 'sum_credit'),
      debit: sumBy(globale, 'sum_debit'),
      lines: globale.length,
      solde: sumBy(globale, 'sum_solde'),
      titres: titre.length,
    };
  }, [balances]);
  const balanceGap = Math.abs(Number(balanceStats.credit || 0) - Number(balanceStats.debit || 0));
  const isBalanced = balanceGap < 1;

  const tabItems = [
    {
      key: '1',
      label: 'Balance globale',
      children: <BGGlobale bg={balances?.globale || []} />,
    },
    {
      key: '2',
      label: 'Balance par titre',
      children: <BGTitre bg={balances?.titre || []} />,
    },
    {
      key: '3',
      label: 'Balance par Trésor',
      children: <BGTresor bg={balances?.tresor || []} />,
    },
    {
      key: '4',
      label: 'Balance par titulaire',
      children: <BGTitulaire bg={balances?.titulaire || []} />,
    },
  ];

  return (
    <Layout hideYellowScrollInfo>
      <Container>
        {/** Obtain 'current' values in Sidebar component */}
        <div className='content'>
          <section className='balance-hero'>
            <div className='hero-copy'>
              <button
                className='back-button'
                type='button'
                onClick={() => history.goBack()}
                aria-label='Retour'
              >
                <FaArrowLeft size={16} />
              </button>
              <div>
                <span className='eyebrow'>Comptabilité titres</span>
                <h1>Balance générale</h1>
                <p>
                  Contrôlez les cumuls débit/crédit, les soldes par titre,
                  Trésor émetteur et titulaire depuis un état unique.
                </p>
              </div>
            </div>
            <div className='hero-actions'>
              <Button className='ghost-action' icon={<FiRefreshCcw />} onClick={fetchData}>
                Rafraîchir
              </Button>
              <ExportFormatButton
                className='primary-action'
                onExport={(format) => exportBGData(balances, format, getExportMetadata(connectedUser))}
              />
            </div>
          </section>

          <section className='kpi-grid'>
            <KpiCard
              icon={<FaDownload />}
              label='Cumul crédit'
              value={`${formatNum(balanceStats.credit) || '--'} XAF`}
            />
            <KpiCard
              icon={<FaDownload />}
              label='Cumul débit'
              value={`${formatNum(balanceStats.debit) || '--'} XAF`}
            />
            <KpiCard
              icon={<FaDownload />}
              label='Solde net'
              value={`${formatNum(balanceStats.solde) || '--'} XAF`}
            />
            <KpiCard
              icon={<FaDownload />}
              label='Titres suivis'
              value={balanceStats.titres || '--'}
            />
          </section>

          <section className='control-panel'>
            <div className='control-copy'>
              <span className='control-icon'><FaBalanceScale /></span>
              <div>
                <strong>Contrôle d’équilibre comptable</strong>
                <span>
                  Écart débit/crédit : {formatNum(balanceGap) || '0'} XAF
                </span>
              </div>
            </div>
            <span className={`control-status ${isBalanced ? 'ok' : 'warning'}`}>
              {isBalanced ? <FaCheckCircle /> : <FaExclamationTriangle />}
              {isBalanced ? 'Balance équilibrée' : 'Écart à contrôler'}
            </span>
          </section>

          <section className='balance-shell'>
            <Tabs
              activeKey={tabIndex}
              type='line'
              onChange={(key: string) => setTabIndex(key)}
              size='small'
              items={tabItems}
            />
          </section>
        </div>
      </Container>
    </Layout>
  );
};
